import React, { useState, useEffect } from 'react';
import Layout from '../../components/admin/Layout';
import api from '../../utils/api';
import { useToast } from '../../context/ToastContext';
import { useAuth } from '../../context/AuthContext';
import './DataTable.css';

export default function AdminUsers() {
  const { showToast } = useToast();
  const { hasPermission, user: currentUser } = useAuth();
  const canManage = hasPermission('admin_users.manage');

  const [users,     setUsers]     = useState([]);
  const [roles,     setRoles]     = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing,   setEditing]   = useState(null);
  const [saving,    setSaving]    = useState(false);

  const [form, setForm] = useState({ firstname:'', lastname:'', email:'', password:'', role_id:'', is_active:1 });
  const [errors, setErrors] = useState({});

  const load = () => {
    setLoading(true);
    Promise.all([api.get('/admin/users'), api.get('/admin/roles')])
      .then(([u, r]) => { setUsers(u.data || []); setRoles(r.data || []); })
      .catch(() => showToast('Failed to load users', 'error'))
      .finally(() => setLoading(false));
  };
  useEffect(load, [showToast]);

  const openCreate = () => {
    setEditing(null);
    setForm({ firstname:'', lastname:'', email:'', password:'', role_id: roles[0]?.id || '', is_active:1 });
    setErrors({});
    setModalOpen(true);
  };
  const openEdit = (u) => {
    setEditing(u);
    setForm({ firstname:u.firstname, lastname:u.lastname, email:u.email, password:'', role_id:u.role_id, is_active:u.is_active });
    setErrors({});
    setModalOpen(true);
  };

  const validate = () => {
    const e = {};
    if (!form.firstname.trim()) e.firstname = 'First name required.';
    if (!form.lastname.trim())  e.lastname  = 'Last name required.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Valid email required.';
    if (!editing && form.password.length < 8) e.password = 'Password must be at least 8 characters.';
    if (editing && form.password && form.password.length < 8) e.password = 'Password must be at least 8 characters.';
    if (!form.role_id) e.role_id = 'Select a role.';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const save = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      if (editing) {
        const payload = { ...form };
        if (!payload.password) delete payload.password;
        await api.put(`/admin/users/${editing.id}`, payload);
        showToast('Admin user updated', 'success');
      } else {
        await api.post('/admin/users', form);
        showToast('Admin user created', 'success');
      }
      setModalOpen(false);
      load();
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to save user', 'error');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`Delete admin user "${u.firstname} ${u.lastname}"?`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      showToast('User deleted', 'success');
      load();
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to delete user', 'error');
    }
  };

  return (
    <Layout>
      <div className="data-table-page">
        <div className="page-header">
          <div>
            <h1>Admin Users</h1>
            <p>Manage administrator accounts and their roles.</p>
          </div>
          {canManage && <button className="btn btn-primary" onClick={openCreate}>+ New Admin</button>}
        </div>

        <div className="table-container">
          {loading ? (
            <div className="table-loading"><div className="spinner" /></div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Last Login</th>
                  <th>Created</th>
                  {canManage && <th></th>}
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.id}>
                    <td>
                      <div className="name-cell">
                        <div className="avatar-sm">{u.firstname?.[0]}{u.lastname?.[0]}</div>
                        <span>
                          {u.firstname} {u.lastname}
                          {u.id === currentUser?.id && <span className="badge badge-primary" style={{marginLeft:'0.5rem'}}>You</span>}
                        </span>
                      </div>
                    </td>
                    <td>{u.email}</td>
                    <td><span className="badge badge-gray">{u.role_name || '—'}</span></td>
                    <td>
                      <span className={`badge badge-${u.is_active ? 'success' : 'danger'}`}>
                        {u.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="text-muted">
                      {u.last_login ? new Date(u.last_login).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' }) : 'Never'}
                    </td>
                    <td className="text-muted">
                      {new Date(u.created_at).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' })}
                    </td>
                    {canManage && (
                      <td>
                        <div className="action-btns">
                          <button className="btn-xs btn-xs-outline" onClick={() => openEdit(u)}>Edit</button>
                          {u.id !== currentUser?.id && (
                            <button className="btn-xs btn-xs-danger" onClick={() => remove(u)}>Delete</button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Modal */}
        {modalOpen && (
          <div className="modal-overlay" onClick={() => setModalOpen(false)}>
            <div className="modal-box" onClick={e => e.stopPropagation()}>
              <h3>{editing ? 'Edit Admin User' : 'Create Admin User'}</h3>
              <div className="form-row-2-eq">
                <div className="form-group">
                  <label className="form-label">First Name <span className="req">*</span></label>
                  <input
                    className={`form-control ${errors.firstname ? 'is-invalid' : ''}`}
                    value={form.firstname}
                    onChange={e => setForm(f => ({ ...f, firstname: e.target.value }))}
                  />
                  {errors.firstname && <span className="invalid-feedback">{errors.firstname}</span>}
                </div>
                <div className="form-group">
                  <label className="form-label">Last Name <span className="req">*</span></label>
                  <input
                    className={`form-control ${errors.lastname ? 'is-invalid' : ''}`}
                    value={form.lastname}
                    onChange={e => setForm(f => ({ ...f, lastname: e.target.value }))}
                  />
                  {errors.lastname && <span className="invalid-feedback">{errors.lastname}</span>}
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Email <span className="req">*</span></label>
                <input
                  className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                  value={form.email}
                  onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                />
                {errors.email && <span className="invalid-feedback">{errors.email}</span>}
              </div>
              <div className="form-group">
                <label className="form-label">
                  Password {editing ? <span className="text-muted">(leave blank to keep current)</span> : <span className="req">*</span>}
                </label>
                <input
                  type="password"
                  className={`form-control ${errors.password ? 'is-invalid' : ''}`}
                  value={form.password}
                  onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                  placeholder={editing ? '••••••••' : 'Minimum 8 characters'}
                />
                {errors.password && <span className="invalid-feedback">{errors.password}</span>}
              </div>
              <div className="form-row-2-eq">
                <div className="form-group">
                  <label className="form-label">Role <span className="req">*</span></label>
                  <select
                    className={`form-control ${errors.role_id ? 'is-invalid' : ''}`}
                    value={form.role_id}
                    onChange={e => setForm(f => ({ ...f, role_id: e.target.value }))}
                  >
                    <option value="">— Select role —</option>
                    {roles.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                  </select>
                  {errors.role_id && <span className="invalid-feedback">{errors.role_id}</span>}
                </div>
                <div className="form-group">
                  <label className="form-label">Status</label>
                  <select
                    className="form-control"
                    value={form.is_active}
                    onChange={e => setForm(f => ({ ...f, is_active: parseInt(e.target.value) }))}
                  >
                    <option value={1}>Active</option>
                    <option value={0}>Inactive</option>
                  </select>
                </div>
              </div>
              <div className="modal-actions">
                <button className="btn btn-outline" onClick={() => setModalOpen(false)}>Cancel</button>
                <button className="btn btn-primary" onClick={save} disabled={saving}>
                  {saving ? <><span className="spinner spinner-sm" /> Saving…</> : editing ? 'Update' : 'Create'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <style>{`
        .form-row-2-eq { display:grid; grid-template-columns:1fr 1fr; gap:0.875rem; }
        @media (max-width:540px){ .form-row-2-eq{ grid-template-columns:1fr; } }
      `}</style>
    </Layout>
  );
}
